<template>
  <div class="portal-ai" :class="{ 'is-collapsed': isCollapsed }">
    <div v-if="isCollapsed" class="portal-ai__fab" @click="togglePanel">
      <i class="el-icon-chat-dot-round"></i>
      <span class="portal-ai__fab-label">助手</span>
    </div>

    <div v-show="!isCollapsed" class="portal-ai__panel">
      <div class="portal-ai__header">
        <div class="portal-ai__title">
          <i class="el-icon-chat-dot-round"></i>
          <span>客户助手</span>
        </div>
        <div class="portal-ai__actions">
          <el-tooltip content="清空对话" placement="bottom">
            <i class="el-icon-delete" @click="clearChat"></i>
          </el-tooltip>
          <el-tooltip content="收起" placement="bottom">
            <i class="el-icon-minus" @click="togglePanel"></i>
          </el-tooltip>
        </div>
      </div>

      <div class="portal-ai__quick">
        <el-button size="mini" plain @click="quickSend('查一下 ')" :disabled="loading">查单</el-button>
        <el-button size="mini" plain @click="quickSend('余额')" :disabled="loading">余额</el-button>
        <el-button size="mini" plain @click="quickSend('美国船期')" :disabled="loading">船期</el-button>
        <el-button size="mini" plain @click="quickSend('你会什么')" :disabled="loading">帮助</el-button>
      </div>

      <div class="portal-ai__chat" ref="chatContainer">
        <div v-for="(msg, index) in messages" :key="index" :class="['portal-ai__msg', msg.role]">
          <div class="portal-ai__bubble" v-html="formatMessage(msg.content)"></div>
          <div class="portal-ai__time">{{ msg.time }}</div>
        </div>
        <div v-if="loading" class="portal-ai__msg ai">
          <div class="portal-ai__bubble"><i class="el-icon-loading"></i> 查询中...</div>
        </div>
      </div>

      <div class="portal-ai__input">
        <el-input
          v-model="inputMessage"
          type="textarea"
          :rows="2"
          placeholder="输入问题，如：查一下运单号 / 余额 / 日本船期"
          resize="none"
          @keydown.native="handleKeydown"
        />
        <el-button
          type="primary"
          size="small"
          :loading="loading"
          :disabled="!inputMessage.trim()"
          @click="sendMessage"
        >发送</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import aiApi, { parseAiResponsePayload } from '@/http/ai'

export default {
  name: 'PortalAiAssistant',
  data() {
    return {
      isCollapsed: true,
      inputMessage: '',
      messages: [],
      loading: false
    }
  },
  methods: {
    togglePanel() {
      this.isCollapsed = !this.isCollapsed
      if (!this.isCollapsed && this.messages.length === 0) {
        this.addWelcome()
      }
    },
    addWelcome() {
      this.messages.push({
        role: 'ai',
        content: '您好！我是客户助手，可帮您查单/轨迹、余额、船期。点上方快捷按钮，或直接输入运单号。',
        time: this.now()
      })
    },
    async sendMessage() {
      const message = (this.inputMessage || '').trim()
      if (!message || this.loading) return
      this.messages.push({ role: 'user', content: message, time: this.now() })
      this.inputMessage = ''
      this.loading = true
      this.scrollBottom()
      try {
        const res = await aiApi.chat(message)
        const parsed = parseAiResponsePayload(res)
        this.messages.push({
          role: 'ai',
          content: parsed.content || '暂无结果',
          time: this.now()
        })
      } catch (e) {
        this.messages.push({
          role: 'ai',
          content: (e && e.message) || '请求失败，请稍后重试',
          time: this.now()
        })
      } finally {
        this.loading = false
        this.scrollBottom()
      }
    },
    quickSend(text) {
      this.inputMessage = text
      if (text === '查一下 ') {
        this.$message.info('请在「查一下」后补上运单号再发送')
        return
      }
      this.sendMessage()
    },
    clearChat() {
      this.$confirm('确定清空对话？', '提示', { type: 'warning' })
        .then(() => {
          this.messages = []
          this.addWelcome()
        })
        .catch(() => {})
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.sendMessage()
      }
    },
    formatMessage(content) {
      if (!content) return ''
      return String(content)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    },
    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    now() {
      const d = new Date()
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.portal-ai {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2000;
}
.portal-ai__fab {
  min-width: 56px;
  height: 56px;
  padding: 0 14px;
  border-radius: 28px;
  background: #1f6feb;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(31, 111, 235, 0.35);
  font-size: 14px;
}
.portal-ai__fab i {
  font-size: 22px;
}
.portal-ai__panel {
  width: 360px;
  height: 520px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}
.portal-ai__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #1f6feb;
  color: #fff;
}
.portal-ai__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}
.portal-ai__actions i {
  margin-left: 12px;
  cursor: pointer;
  opacity: 0.9;
}
.portal-ai__quick {
  padding: 8px 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-bottom: 1px solid #f0f0f0;
}
.portal-ai__chat {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f7f8fa;
}
.portal-ai__msg {
  margin-bottom: 10px;
}
.portal-ai__msg.user {
  text-align: right;
}
.portal-ai__bubble {
  display: inline-block;
  max-width: 88%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
  background: #fff;
  border: 1px solid #e8e8e8;
}
.portal-ai__msg.user .portal-ai__bubble {
  background: #e8f1ff;
  border-color: #c9dcff;
}
.portal-ai__time {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}
.portal-ai__input {
  padding: 10px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 8px;
  align-items: flex-end;
}
.portal-ai__input .el-textarea {
  flex: 1;
}
</style>
